import React, { Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { renderRoutes } from './routes';
import { ChakraProvider } from '@chakra-ui/react';
import { Auth0Provider, Auth0Context } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Global, css } from '@emotion/react';
import { WLProvider } from '@providers';

import * as Sentry from '@sentry/react';
import Trackers from './Trackers';
// import "@cincel.digital/react/";
// import i18n (needs to be bundled ;))
import './i18n';
import { Spinner, ServiceWorkerWrapper } from './components';

const GlobalStyles = css`
  /*
    This will hide the focus indicator if the element receives focus    via the mouse,
    but it will still show up on keyboard focus.
  */
  .js-focus-visible :focus:not([data-focus-visible-added]) {
    outline: none;
    box-shadow: none;
  }
`;

declare global {
  interface Window {
    Core: any;
    PDFNet: any;
  }
}

window.Core = window.Core || {};
window.PDFNet = window.PDFNet || {};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN;
const AUTH0_CLIENTID = process.env.REACT_APP_AUTH0_CLIENTID;

// Mock Auth0 for insecure origins (local IP testing)
const isSecureOrigin =
  window.location.protocol === 'https:' ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

const mockAuth0Context = {
  isAuthenticated: false,
  user: undefined,
  isLoading: false,
  loginWithPopup: async () => { },
  logout: () => { },
  getAccessTokenSilently: async () => '',
  loginWithRedirect: async () => { },
  getIdTokenClaims: async () => undefined,
  getAccessTokenWithPopup: async () => '',
  handleRedirectCallback: async () => { },
  buildAuthorizeUrl: async () => '',
  buildLogoutUrl: () => '',
} as any;

function App() {
  return <MainApp />;
}

const AppContent = () => (
  <QueryClientProvider client={queryClient}>
    <Suspense fallback={<Spinner />}>
      <WLProvider>
        <ServiceWorkerWrapper />
        <Router>{renderRoutes()}</Router>
      </WLProvider>
    </Suspense>
  </QueryClientProvider>
);

function MainApp() {
  const auth0ProviderProps = {
    domain: AUTH0_DOMAIN as string,
    clientId: AUTH0_CLIENTID as string,
    redirectUri: window.location.origin,
  };

  return (
    <>
      <Trackers>
        <ChakraProvider>
          <Global styles={GlobalStyles} />
          {isSecureOrigin ? (
            <Auth0Provider {...auth0ProviderProps}>
              <AppContent />
            </Auth0Provider>
          ) : (
            <Auth0Context.Provider value={mockAuth0Context}>
              <AppContent />
            </Auth0Context.Provider>
          )}
        </ChakraProvider>
      </Trackers>
    </>
  );
}

export default Sentry.withProfiler(App);
